import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Camera, Upload, Edit, Trash2, Eye, FileUp, Download, CheckCircle, AlertCircle, CheckSquare, Square, Printer } from 'lucide-react';
import Webcam from 'react-webcam';
import { useAuth } from '../AuthContext';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import PaginationControls from '../components/PaginationControls';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { getStates, getDistricts } from '../data/indianStatesDistricts';

const DEFAULT_PAGE_SIZE = 50;

// Print helper — opens a new window with a clean printable list
const printEntrepreneursList = (entrepreneurs, branches, selectedBranchFilter) => {
  const branchName = selectedBranchFilter === 'all'
    ? 'All Branches'
    : (branches.find(b => b.id === selectedBranchFilter)?.name || '-');
  const rows = entrepreneurs.map((e, i) => {
    const br = branches.find(b => b.id === e.branch_id);
    return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
      <td>${i + 1}</td>
      <td>${e.enrollment_no || '-'}</td>
      <td>${e.name || '-'}</td>
      <td>${br?.name || '-'}</td>
      <td>${e.mobile || '-'}</td>
      <td>${e.enterprise?.name || '-'}</td>
      <td>${e.enterprise?.operation_type || '-'}</td>
      <td>${e.enterprise?.loan_type || '-'}</td>
    </tr>`;
  }).join('');
  const html = `<!doctype html><html><head><title>Entrepreneurs — WWAGS MIS</title>
  <style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{color:#047857;font-size:20px;margin:0 0 4px}
    .meta{color:#64748b;font-size:12px;margin-bottom:14px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#047857;color:#fff;padding:8px;text-align:left}
    td{padding:6px 8px;border-bottom:1px solid #e2e8f0}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>
    <h1>WWAGS MIS — Entrepreneurs List</h1>
    <p class="meta">Branch: <b>${branchName}</b> &nbsp;•&nbsp; Total: <b>${entrepreneurs.length}</b> &nbsp;•&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</p>
    <table>
      <thead><tr><th>#</th><th>Enrollment No</th><th>Name</th><th>Branch</th><th>Mobile</th><th>Enterprise</th><th>Operation</th><th>Loan Type</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:20px">No entrepreneurs</td></tr>'}</tbody>
    </table>
  </body></html>`;
  const w = window.open('', '_blank');
  if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
};

// Avatar component to handle photo display with error fallback
const EntrepreneurAvatar = ({ entrepreneur }) => {
  const [imgError, setImgError] = useState(false);
  const initials = entrepreneur.name.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');
  
  if (!entrepreneur.photo_url || imgError) {
    return (
      <div className="w-10 h-12 rounded bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-lg border border-slate-300">
        {initials}
      </div>
    );
  }
  
  const photoSrc = entrepreneur.photo_url.startsWith('data:') 
    ? entrepreneur.photo_url 
    : `${process.env.REACT_APP_BACKEND_URL}${entrepreneur.photo_url}`;
  
  return (
    <img
      src={photoSrc}
      alt={entrepreneur.name}
      className="w-10 h-12 rounded object-cover border border-slate-200"
      onError={() => setImgError(true)}
    />
  );
};

const Entrepreneurs = () => {
  const { hasPermission, isAdmin, isAdminOnly, user } = useAuth();
  const navigate = useNavigate();
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntrepreneur, setEditingEntrepreneur] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const webcamRef = useRef(null);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Bulk upload state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBranch, setBulkBranch] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Custom relationship state for "Other" option
  const [customRelationship, setCustomRelationship] = useState('');
  
  const relationshipOptions = ['Self', 'Mother', 'Sister', 'Daughter', 'Daughter-in-law', 'Relative', 'Other'];

  // Check if selected branch is "Leirik Microfinance" (BR10) - fields become non-mandatory
  const isLeirikBranch = () => {
    const selectedBranch = branches.find(b => b.id === formData.branch_id);
    return selectedBranch?.code === 'BR10';
  };

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    district: '',
    state: '',
    block: '',
    gram_panchayat: '',
    village: '',
    pincode: '',
    mobile: '',
    pan_number: '',
    aadhaar_number: '',
    gender: 'Female',
    caste: 'General',
    shg_vlf_name: '',
    vo_name: '',
    clf_name: '',
    shg_member_name: '',
    shg_member_relation: '',
    branch_id: '',
    crp_ep_id: '',
    enterprise: {
      name: '',
      type: '',
      operation_type: 'Manufacturing',
      is_existing: false,
      loan_type: 'CEF',
      proposed_loan_amount: 0,
      product_item: '',
      ownership_structure: 'Self',
      govt_license: '',
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (branches.length > 0) {
      fetchEntrepreneurs();
    }
  }, [selectedBranchFilter, page, pageSize]);

  // Real-time auto-refresh: re-fetch list every 2 minutes + on window focus (lighter interval for large lists)
  useAutoRefresh(() => fetchEntrepreneurs(), { intervalMs: 120000 });

  const buildListParams = (pageArg) => {
    const params = {
      search: searchQuery || undefined,
      skip: pageArg * pageSize,
      limit: pageSize,
      include_photos: false,
    };
    if (selectedBranchFilter !== 'all') {
      params.branch_id = selectedBranchFilter;
    }
    return params;
  };

  const fetchData = async () => {
    try {
      const [entrepreneursRes, branchesRes, staffRes] = await Promise.all([
        api.get('/entrepreneurs', { params: buildListParams(page) }),
        api.get('/branches'),
        api.get('/staff', { params: { limit: 5000 } }),
      ]);
      setEntrepreneurs(entrepreneursRes.data);
      setTotalCount(parseInt(entrepreneursRes.headers['x-total-count'] || '0', 10) || entrepreneursRes.data.length);
      setBranches(branchesRes.data);
      setStaffList(staffRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Entrepreneur GET also gives the branch filter param
  const fetchEntrepreneurs = async (pageOverride) => {
    const currentPage = pageOverride !== undefined ? pageOverride : page;
    try {
      const response = await api.get('/entrepreneurs', { params: buildListParams(currentPage) });
      setEntrepreneurs(response.data);
      setTotalCount(parseInt(response.headers['x-total-count'] || '0', 10) || response.data.length);
    } catch (error) {
      toast.error('Failed to fetch entrepreneurs');
    } finally {
      setLoading(false);
    }
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedPhoto(imageSrc);
    setShowWebcam(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Prepare the form data with resolved relationship value
      const dataToSubmit = {
        ...formData,
        shg_member_relation: formData.shg_member_relation === 'Other' ? customRelationship : formData.shg_member_relation
      };
      
      const formDataToSend = new FormData();
      formDataToSend.append('data', JSON.stringify(dataToSubmit));
      if (capturedPhoto) {
        // Only re-upload if it's a new photo (not the existing one from DB)
        const isExistingPhoto = editingEntrepreneur?.photo_url && capturedPhoto === editingEntrepreneur.photo_url;
        if (!isExistingPhoto) {
          formDataToSend.append('photo', capturedPhoto);
        }
      }

      if (editingEntrepreneur) {
        // Update existing entrepreneur
        await api.put(`/entrepreneurs/${editingEntrepreneur.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Entrepreneur updated successfully');
      } else {
        // Create new entrepreneur
        await api.post('/entrepreneurs', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Entrepreneur added successfully');
      }

      setDialogOpen(false);
      setEditingEntrepreneur(null);
      resetForm();
      fetchEntrepreneurs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entrepreneur');
    }
  };

  const openEditDialog = async (entrepreneur) => {
    // Since list omits photo for performance, fetch the full record to load its photo
    let full = entrepreneur;
    try {
      const r = await api.get(`/entrepreneurs/${entrepreneur.id}`);
      full = r.data || entrepreneur;
    } catch { /* fall back to list row without photo */ }
    setEditingEntrepreneur(full);

    // Check if the relationship is a custom value (not in predefined options)
    const relation = full.shg_member_relation || '';
    const isCustomRelation = relation && !relationshipOptions.includes(relation);

    setFormData({
      name: full.name || '',
      address: full.address || '',
      district: full.district || '',
      state: full.state || '',
      block: full.block || '',
      gram_panchayat: full.gram_panchayat || '',
      village: full.village || '',
      pincode: full.pincode || '',
      mobile: full.mobile || '',
      pan_number: full.pan_number || '',
      aadhaar_number: full.aadhaar_number || '',
      gender: full.gender || 'Female',
      caste: full.caste || 'General',
      shg_vlf_name: full.shg_vlf_name || '',
      vo_name: full.vo_name || '',
      clf_name: full.clf_name || '',
      shg_member_name: full.shg_member_name || '',
      shg_member_relation: isCustomRelation ? 'Other' : relation,
      branch_id: full.branch_id || '',
      crp_ep_id: full.crp_ep_id || '',
      enterprise: {
        name: full.enterprise?.name || '',
        type: full.enterprise?.type || '',
        operation_type: full.enterprise?.operation_type || 'Manufacturing',
        is_existing: full.enterprise?.is_existing || false,
        loan_type: full.enterprise?.loan_type || 'CEF',
        proposed_loan_amount: full.enterprise?.proposed_loan_amount || 0,
        product_item: full.enterprise?.product_item || '',
        ownership_structure: full.enterprise?.ownership_structure || 'Self',
        govt_license: full.enterprise?.govt_license || '',
      },
    });
    setCustomRelationship(isCustomRelation ? relation : '');
    setCapturedPhoto(full.photo_url || null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEntrepreneur(null);
    setCustomRelationship('');
    setFormData({
      name: '',
      address: '',
      district: '',
      state: '',
      block: '',
      gram_panchayat: '',
      village: '',
      pincode: '',
      mobile: '',
      pan_number: '',
      aadhaar_number: '',
      gender: 'Female',
      caste: 'General',
      shg_vlf_name: '',
      vo_name: '',
      clf_name: '',
      shg_member_name: '',
      shg_member_relation: '',
      branch_id: '',
      crp_ep_id: '',
      enterprise: {
        name: '',
        type: '',
        operation_type: 'Manufacturing',
        is_existing: false,
        loan_type: 'CEF',
        proposed_loan_amount: 0,
        product_item: '',
        ownership_structure: 'Self',
        govt_license: '',
      },
    });
    setCapturedPhoto(null);
  };

  const handleDelete = async (id) => {
    if (!isAdmin()) {
      toast.error('Only Admin can delete entrepreneurs');
      return;
    }
    try {
      await api.delete(`/entrepreneurs/${id}`);
      toast.success('Entrepreneur deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entrepreneur');
    }
  };
  
  const openDeleteDialog = (entrepreneur) => {
    setItemToDelete(entrepreneur);
    setDeleteDialogOpen(true);
  };

  // ==== Multi-select helpers ====
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === entrepreneurs.length && entrepreneurs.length > 0) return new Set();
      return new Set(entrepreneurs.map(e => e.id));
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const resp = await api.post('/entrepreneurs/bulk-delete', { ids: Array.from(selectedIds) });
      const deleted = resp.data?.deleted || 0;
      toast.success(`${deleted} entrepreneur(s) deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk delete');
    } finally {
      setBulkDeleting(false);
      setBulkDeleteOpen(false);
      clearSelection();
      fetchData();
    }
  };

  // Range delete (admin cleanup tool)
  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [rangePreview, setRangePreview] = useState(null);
  const [rangeDeleting, setRangeDeleting] = useState(false);

  const handleRangeDelete = async () => {
    const f = parseInt(rangeFrom, 10);
    const t = parseInt(rangeTo, 10);
    if (!f || !t || f > t) { toast.error('Invalid range'); return; }
    if (!window.confirm(`Permanently delete ALL entrepreneurs from ENR-${String(f).padStart(5,'0')} to ENR-${String(t).padStart(5,'0')}? This cannot be undone.`)) return;
    setRangeDeleting(true);
    try {
      const resp = await api.post('/entrepreneurs/bulk-delete-by-range', { from_no: f, to_no: t });
      toast.success(`Deleted ${resp.data?.deleted || 0} entrepreneurs in range`);
      setRangeDialogOpen(false);
      setRangeFrom(''); setRangeTo(''); setRangePreview(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete range');
    } finally {
      setRangeDeleting(false);
    }
  };

  // Bulk upload handlers
  const downloadTemplate = async () => {
    try {
      const response = await api.get('/entrepreneur-bulk/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'entrepreneur_bulk_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Please select a file'); return; }
    if (isAdmin() && !bulkBranch) { toast.error('Please select a branch'); return; }

    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('branch_id', isAdmin() ? bulkBranch : 'auto');
      const response = await api.post('/entrepreneur-bulk/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} entrepreneurs imported successfully`);
        fetchEntrepreneurs();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkFile(null);
    setBulkBranch('');
    setBulkResult(null);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="entrepreneurs-page">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Entrepreneurs</h1>
          <p className="text-sm text-slate-600">Manage entrepreneur and enterprise enrollments</p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkDialogOpen(true)}
            data-testid="bulk-upload-button"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            <FileUp className="w-4 h-4 mr-2" />
            Bulk Upload
          </Button>
          {isAdminOnly() && (
            <Button
              variant="outline"
              onClick={() => setRangeDialogOpen(true)}
              data-testid="range-delete-button"
              className="border-red-300 text-red-700 hover:bg-red-50"
              title="Delete entrepreneurs by Enrollment No range (admin cleanup tool)"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Range Delete
            </Button>
          )}

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-entrepreneur-button"
              className="bg-emerald-700 hover:bg-emerald-800 text-white w-full sm:w-auto"
              onClick={() => { setEditingEntrepreneur(null); resetForm(); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entrepreneur
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl w-[95vw] md:w-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEntrepreneur ? 'Edit Entrepreneur' : 'Add New Entrepreneur'}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>Photo</Label>
                  <div className="flex gap-4 items-center">
                    {capturedPhoto ? (
                      <div className="relative">
                        <img
                          src={capturedPhoto}
                          alt="Captured"
                          className="w-32 h-32 rounded-lg object-cover border-2 border-slate-200"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2"
                          onClick={() => setCapturedPhoto(null)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : showWebcam ? (
                      <div className="space-y-2">
                        <Webcam
                          audio={false}
                          ref={webcamRef}
                          screenshotFormat="image/jpeg"
                          className="w-64 h-48 rounded-lg border-2 border-slate-200"
                        />
                        <div className="flex gap-2">
                          <Button type="button" size="sm" onClick={capturePhoto}>
                            Capture
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setShowWebcam(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowWebcam(true)}
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Take Photo
                        </Button>
                        <Label htmlFor="photo-upload" className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50">
                            <Upload className="w-4 h-4" />
                            Upload Photo
                          </div>
                          <input
                            id="photo-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                        </Label>
                      </>
                    )}
                  </div>
                </div>

                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Branch Selection - Show for all users */}
                  <div className="space-y-2">
                    <Label htmlFor="branch_id">Branch *</Label>
                    <Select
                      value={formData.branch_id}
                      onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                    >
                      <SelectTrigger data-testid="entrepreneur-branch-select">
                        <SelectValue placeholder="Select Branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name} ({branch.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CRP-EP Selection from Staff List — selected branch's CRP-EPs shown first, all others fallback */}
                  <div className="space-y-2">
                    <Label htmlFor="crp_ep_id">Promoted by CRP-EP</Label>
                    <Select
                      value={formData.crp_ep_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, crp_ep_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="entrepreneur-crp-ep-select">
                        <SelectValue placeholder="Select CRP-EP (Optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- None --</SelectItem>
                        {(() => {
                          const all = staffList.filter(s => s.staff_type?.toLowerCase().includes('crp'));
                          const inBranch = all.filter(s => formData.branch_id && s.branch_id === formData.branch_id);
                          const others = all.filter(s => !inBranch.includes(s));
                          // Logged-in CRP-EP user — always include as "Myself" if they don't already have a staff record
                          const selfOption = (user?.role === 'CRP-EP' && !all.some(s => (s.name || '').trim() === (user?.name || '').trim()))
                            ? <SelectItem key="self" value="self">Myself — {user?.name || user?.email} (CRP-EP)</SelectItem>
                            : null;
                          return (
                            <>
                              {selfOption}
                              {inBranch.length > 0 && (
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-emerald-700 font-bold bg-emerald-50">In this branch</div>
                              )}
                              {inBranch.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name} ({s.staff_type})</SelectItem>
                              ))}
                              {others.length > 0 && (
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 font-bold bg-slate-50">Other branches</div>
                              )}
                              {others.map(s => {
                                const br = branches.find(b => b.id === s.branch_id);
                                return (
                                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.staff_type}{br ? ` · ${br.code || br.name}` : ''})</SelectItem>
                                );
                              })}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Name {!isLeirikBranch() && '*'}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLeirikBranch()}
                      data-testid="entrepreneur-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number {!isLeirikBranch() && '*'}</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      required={!isLeirikBranch()}
                      data-testid="entrepreneur-mobile-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pan_number">PAN Number</Label>
                    <Input
                      id="pan_number"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                      placeholder="e.g. ABCDE1234F"
                      maxLength={10}
                      data-testid="entrepreneur-pan-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aadhaar_number">Aadhaar Number</Label>
                    <Input
                      id="aadhaar_number"
                      value={formData.aadhaar_number}
                      onChange={(e) => setFormData({ ...formData, aadhaar_number: e.target.value.replace(/\D/g, '') })}
                      placeholder="e.g. 123456789012"
                      maxLength={12}
                      data-testid="entrepreneur-aadhaar-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender {!isLeirikBranch() && '*'}</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger data-testid="entrepreneur-gender-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="caste">Caste {!isLeirikBranch() && '*'}</Label>
                    <Select
                      value={formData.caste}
                      onValueChange={(value) => setFormData({ ...formData, caste: value })}
                    >
                      <SelectTrigger data-testid="entrepreneur-caste-select">
                        <SelectValue placeholder="Select caste" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SC">SC</SelectItem>
                        <SelectItem value="ST">ST</SelectItem>
                        <SelectItem value="OBC">OBC</SelectItem>
                        <SelectItem value="General">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address {!isLeirikBranch() && '*'}</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required={!isLeirikBranch()}
                    data-testid="entrepreneur-address-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value, district: '' })}
                    >
                      <SelectTrigger data-testid="entrepreneur-state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {getStates().map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="district">District</Label>
                    <Select
                      value={formData.district}
                      onValueChange={(value) => setFormData({ ...formData, district: value })}
                      disabled={!formData.state}
                    >
                      <SelectTrigger data-testid="entrepreneur-district-select">
                        <SelectValue placeholder={formData.state ? "Select district" : "Select state first"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {getDistricts(formData.state).map((district) => (
                          <SelectItem key={district} value={district}>{district}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Detailed Location (matches WWAGS Excel Business Plan template) */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="block">Block</Label>
                    <Input id="block" value={formData.block} onChange={(e) => setFormData({ ...formData, block: e.target.value })} data-testid="entrepreneur-block" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gram_panchayat">Gram Panchayat</Label>
                    <Input id="gram_panchayat" value={formData.gram_panchayat} onChange={(e) => setFormData({ ...formData, gram_panchayat: e.target.value })} data-testid="entrepreneur-gp" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="village">Village</Label>
                    <Input id="village" value={formData.village} onChange={(e) => setFormData({ ...formData, village: e.target.value })} data-testid="entrepreneur-village" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode</Label>
                    <Input id="pincode" value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} data-testid="entrepreneur-pincode" maxLength={6} />
                  </div>
                </div>

                {/* SHG / VO / CLF Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900">SHG / VO / CLF Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="shg_vlf_name">Name of SHG {!isLeirikBranch() && '*'}</Label>
                      <Input
                        id="shg_vlf_name"
                        value={formData.shg_vlf_name}
                        onChange={(e) => setFormData({ ...formData, shg_vlf_name: e.target.value })}
                        required={!isLeirikBranch()}
                      />
                    </div>

                    {/* VO / CLF: required for new entrepreneurs; optional when editing older records that never captured them */}
                    <div className="space-y-2">
                      <Label htmlFor="vo_name">Name of VO {!isLeirikBranch() && !editingEntrepreneur && '*'}</Label>
                      <Input
                        id="vo_name"
                        value={formData.vo_name}
                        onChange={(e) => setFormData({ ...formData, vo_name: e.target.value })}
                        required={!isLeirikBranch() && !editingEntrepreneur}
                        data-testid="entrepreneur-vo-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="clf_name">Name of CLF {!isLeirikBranch() && !editingEntrepreneur && '*'}</Label>
                      <Input
                        id="clf_name"
                        value={formData.clf_name}
                        onChange={(e) => setFormData({ ...formData, clf_name: e.target.value })}
                        required={!isLeirikBranch() && !editingEntrepreneur}
                        data-testid="entrepreneur-clf-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="shg_member_name">SHG Member Name {!isLeirikBranch() && '*'}</Label>
                      <Input
                        id="shg_member_name"
                        value={formData.shg_member_name}
                        onChange={(e) => setFormData({ ...formData, shg_member_name: e.target.value })}
                        required={!isLeirikBranch()}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="shg_member_relation">Relationship {!isLeirikBranch() && '*'}</Label>
                      <Select
                        value={formData.shg_member_relation}
                        onValueChange={(value) => {
                          setFormData({ ...formData, shg_member_relation: value });
                          if (value !== 'Other') setCustomRelationship('');
                        }}
                      >
                        <SelectTrigger data-testid="entrepreneur-relationship-select">
                          <SelectValue placeholder="Select Relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          {relationshipOptions.map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.shg_member_relation === 'Other' && (
                        <Input
                          placeholder="Enter relationship"
                          value={customRelationship}
                          onChange={(e) => setCustomRelationship(e.target.value)}
                          required={!isLeirikBranch()}
                          className="mt-2"
                          data-testid="entrepreneur-custom-relationship-input"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Enterprise Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900">Enterprise Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="enterprise_name">Enterprise Name {!isLeirikBranch() && '*'}</Label>
                      <Input
                        id="enterprise_name"
                        value={formData.enterprise.name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise: { ...formData.enterprise, name: e.target.value },
                          })
                        }
                        required={!isLeirikBranch()}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="enterprise_type">Enterprise Type {!isLeirikBranch() && '*'}</Label>
                      <Input
                        id="enterprise_type"
                        value={formData.enterprise.type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise: { ...formData.enterprise, type: e.target.value },
                          })
                        }
                        required={!isLeirikBranch()}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="operation_type">Operation Type {!isLeirikBranch() && '*'}</Label>
                      <Select
                        value={formData.enterprise.operation_type}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            enterprise: { ...formData.enterprise, operation_type: value },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="Service">Service</SelectItem>
                          <SelectItem value="Trading">Trading</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="is_existing">Status *</Label>
                      <Select
                        value={formData.enterprise.is_existing ? 'existing' : 'new'}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            enterprise: { ...formData.enterprise, is_existing: value === 'existing' },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="existing">Existing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="loan_type">Loan Type</Label>
                      <Select
                        value={formData.enterprise.loan_type}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            enterprise: { ...formData.enterprise, loan_type: value },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CEF">CEF</SelectItem>
                          <SelectItem value="CIF">CIF</SelectItem>
                          <SelectItem value="Bank">Bank</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="proposed_loan_amount">Proposed Loan Amount</Label>
                      <Input
                        id="proposed_loan_amount"
                        type="number"
                        value={formData.enterprise.proposed_loan_amount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise: {
                              ...formData.enterprise,
                              proposed_loan_amount: parseFloat(e.target.value) || 0,
                            },
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product_item">Product / Item</Label>
                      <Input
                        id="product_item"
                        placeholder="e.g. DRESSES CLOTH ITEMS"
                        value={formData.enterprise.product_item}
                        onChange={(e) => setFormData({ ...formData, enterprise: { ...formData.enterprise, product_item: e.target.value } })}
                        data-testid="enterprise-product-item"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ownership_structure">Ownership Structure</Label>
                      <Select
                        value={formData.enterprise.ownership_structure}
                        onValueChange={(value) => setFormData({ ...formData, enterprise: { ...formData.enterprise, ownership_structure: value } })}
                      >
                        <SelectTrigger data-testid="enterprise-ownership-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Self">Self</SelectItem>
                          <SelectItem value="Family">Family</SelectItem>
                          <SelectItem value="Partnership">Partnership</SelectItem>
                          <SelectItem value="Group">Group</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="govt_license">Govt. License Available</Label>
                      <Input
                        id="govt_license"
                        placeholder="License No. or NA"
                        value={formData.enterprise.govt_license}
                        onChange={(e) => setFormData({ ...formData, enterprise: { ...formData.enterprise, govt_license: e.target.value } })}
                        data-testid="enterprise-govt-license"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-entrepreneur-button"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    {editingEntrepreneur ? 'Update Entrepreneur' : 'Add Entrepreneur'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!open) closeBulkDialog(); else setBulkDialogOpen(true); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Entrepreneurs</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Step 1: Download Template */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Step 1: Download Template</h4>
              <p className="text-xs text-blue-700 mb-3">Download the Excel template, fill in entrepreneur details, and upload below.</p>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-button">
                <Download className="w-4 h-4 mr-2" />
                Download Template (.xlsx)
              </Button>
            </div>

            {/* Step 2: Select Branch */}
            {isAdmin() && (
              <div>
                <Label className="text-sm font-medium">Step 2: Select Branch *</Label>
                <Select value={bulkBranch} onValueChange={setBulkBranch}>
                  <SelectTrigger className="mt-1" data-testid="bulk-branch-select">
                    <SelectValue placeholder="Select branch for all entries" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 3: Upload File */}
            <div>
              <Label className="text-sm font-medium">{isAdmin() ? 'Step 3' : 'Step 2'}: Upload File *</Label>
              <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-emerald-400 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => { setBulkFile(e.target.files[0]); setBulkResult(null); }}
                  className="hidden"
                  id="bulk-file-input"
                  data-testid="bulk-file-input"
                />
                <label htmlFor="bulk-file-input" className="cursor-pointer">
                  <FileUp className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  {bulkFile ? (
                    <p className="text-sm font-medium text-emerald-700">{bulkFile.name}</p>
                  ) : (
                    <p className="text-sm text-slate-500">Click to select Excel (.xlsx) or CSV file</p>
                  )}
                </label>
              </div>
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleBulkUpload}
              disabled={bulkUploading || !bulkFile || (isAdmin() && !bulkBranch)}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="bulk-upload-submit"
            >
              {bulkUploading ? 'Uploading...' : 'Upload & Import'}
            </Button>

            {/* Results */}
            {bulkResult && (
              <div className={`p-4 rounded-lg border ${bulkResult.imported > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  {bulkResult.imported > 0 ? (
                    <><CheckCircle className="w-4 h-4 text-emerald-600" /> Upload Complete</>
                  ) : (
                    <><AlertCircle className="w-4 h-4 text-red-600" /> Upload Issues</>
                  )}
                </h4>
                <div className="grid grid-cols-3 gap-2 text-center mb-2">
                  <div className="bg-white rounded p-2">
                    <p className="text-lg font-bold text-slate-900">{bulkResult.total}</p>
                    <p className="text-xs text-slate-500">Total Rows</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-lg font-bold text-emerald-700">{bulkResult.imported}</p>
                    <p className="text-xs text-emerald-600">Imported</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-lg font-bold text-red-600">{bulkResult.skipped}</p>
                    <p className="text-xs text-red-500">Skipped</p>
                  </div>
                </div>
                {bulkResult.errors && bulkResult.errors.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    {bulkResult.errors.map((err, i) => (
                      <p key={`bulk-err-${i}-${(err || '').slice(0, 20)}`} className="text-xs text-red-600 py-0.5">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Search and Branch Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            placeholder="Search entrepreneurs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') { setPage(0); fetchEntrepreneurs(0); } }}
            className="pl-10"
            data-testid="search-entrepreneurs-input"
          />
        </div>
        {isAdmin() && (
          <Select value={selectedBranchFilter} onValueChange={(val) => { setSelectedBranchFilter(val); setPage(0); }}>
            <SelectTrigger className="w-full sm:w-52" data-testid="branch-filter-entrepreneurs">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const params = { search: searchQuery || undefined, limit: 10000 };
              if (selectedBranchFilter !== 'all') params.branch_id = selectedBranchFilter;
              const r = await api.get('/entrepreneurs', { params });
              printEntrepreneursList(r.data, branches, selectedBranchFilter);
            } catch (e) {
              toast.error('Failed to load data for printing');
            }
          }}
          data-testid="print-entrepreneurs-button"
          className="border-slate-300"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-1 min-h-[calc(100vh-280px)]">
          <div className="overflow-auto h-full max-h-[calc(100vh-280px)]">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {isAdminOnly() && (
                    <th className="px-3 py-3 text-left w-10">
                      <button
                        type="button"
                        onClick={toggleSelectAll}
                        className="text-slate-600 hover:text-emerald-700"
                        title={selectedIds.size === entrepreneurs.length && entrepreneurs.length > 0 ? 'Deselect all' : 'Select all'}
                        data-testid="select-all-entrepreneurs"
                      >
                        {selectedIds.size === entrepreneurs.length && entrepreneurs.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-emerald-700" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Photo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Enrollment No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Mobile
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Enterprise
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entrepreneurs.length === 0 ? (
                  <tr>
                    <td colSpan={8 + (isAdminOnly() ? 1 : 0)} className="px-4 py-8 text-center text-slate-500">
                      No entrepreneurs found
                    </td>
                  </tr>
                ) : (
                  entrepreneurs.map((entrepreneur) => {
                    const branch = branches.find(b => b.id === entrepreneur.branch_id);
                    const isSelected = selectedIds.has(entrepreneur.id);
                    return (
                    <tr key={entrepreneur.id} className={`${isSelected ? 'bg-emerald-50' : 'hover:bg-slate-50'} transition-colors`} data-testid="entrepreneur-row">
                      {isAdminOnly() && (
                        <td className="px-3 py-3 w-10">
                          <button
                            type="button"
                            onClick={() => toggleSelect(entrepreneur.id)}
                            className="text-slate-500 hover:text-emerald-700"
                            data-testid={`select-${entrepreneur.id}`}
                          >
                            {isSelected ? <CheckSquare className="w-4 h-4 text-emerald-700" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <EntrepreneurAvatar entrepreneur={entrepreneur} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          {entrepreneur.enrollment_no || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{entrepreneur.name}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-800 border border-purple-200">
                          {branch?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{entrepreneur.mobile}</td>
                      <td className="px-4 py-3 text-sm text-slate-900">{entrepreneur.enterprise.name}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                          {entrepreneur.enterprise.operation_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/entrepreneurs/${entrepreneur.id}`)}
                            data-testid="view-entrepreneur-button"
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(entrepreneur)}
                            data-testid="edit-entrepreneur-button"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          {isAdminOnly() && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteDialog(entrepreneur)}
                              data-testid="delete-entrepreneur-button"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination controls (page size selector + prev/next) */}
      {!loading && (
        <PaginationControls
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
          label="entrepreneurs"
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => itemToDelete && handleDelete(itemToDelete.id)}
        title="Delete Entrepreneur"
        description="Are you sure you want to delete this entrepreneur? This action cannot be undone and will permanently remove all associated data."
        itemName={itemToDelete?.name}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedIds.size} Entrepreneur${selectedIds.size === 1 ? '' : 's'}`}
        description={`Are you sure you want to permanently delete ${selectedIds.size} selected entrepreneur${selectedIds.size === 1 ? '' : 's'}? This action cannot be undone and will remove all associated loan, photo, and enterprise data.`}
        itemName={`${selectedIds.size} record${selectedIds.size === 1 ? '' : 's'}`}
      />

      {/* Range Delete Dialog (admin cleanup tool) */}
      <Dialog open={rangeDialogOpen} onOpenChange={setRangeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete Entrepreneurs by Enrollment Range</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
              <strong>Warning:</strong> This permanently deletes all entrepreneurs whose enrollment number falls in the given range (e.g. ENR-00092 to ENR-00500). Used to clean up bulk-upload mistakes. Cannot be undone.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">From Enrollment No.</Label>
                <Input type="number" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} placeholder="e.g. 92" data-testid="range-from" />
                <p className="text-[10px] text-slate-500">ENR-{(parseInt(rangeFrom,10)||0).toString().padStart(5,'0')}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To Enrollment No.</Label>
                <Input type="number" value={rangeTo} onChange={e => setRangeTo(e.target.value)} placeholder="e.g. 500" data-testid="range-to" />
                <p className="text-[10px] text-slate-500">ENR-{(parseInt(rangeTo,10)||0).toString().padStart(5,'0')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setRangeDialogOpen(false); setRangeFrom(''); setRangeTo(''); }}>Cancel</Button>
              <Button onClick={handleRangeDelete} disabled={rangeDeleting || !rangeFrom || !rangeTo} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-range-delete">
                {rangeDeleting ? 'Deleting...' : 'Delete Range'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating multi-select action bar */}
      {isAdminOnly() && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-slate-200 shadow-2xl rounded-full px-5 py-2.5 flex items-center gap-3" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold text-slate-700">
            {selectedIds.size} selected
          </span>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="text-slate-600 hover:text-slate-800 h-8 text-xs">
            Clear
          </Button>
          <div className="w-px h-5 bg-slate-200" />
          <Button
            size="sm"
            onClick={() => setBulkDeleteOpen(true)}
            disabled={bulkDeleting}
            className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs font-semibold"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size}`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Entrepreneurs;